import { useState, useMemo, useEffect } from "react"
import { getHeaders } from "./headers"
import { useTranslation } from "react-i18next"
import { Flex, Dropdown } from "antd"
import { Text } from "ui/Typography"
import GroupTags from "../GroupTags"
import { MoreOutlined } from "@ant-design/icons"
import styles from "./index.module.css"
import DataTable from "ui/DataTable"
import Avatar from "components/common/Avatar"
import BulkActions from "./BulkActions"
import PropTypes from "prop-types"
import { useNavigate } from "react-router-dom"
import { routes } from "router/routes"
import { SyncBulkGroup, CheckClientDelete, deleteClient, cleanClient } from "store/clients"
import { useDispatch, useSelector } from "react-redux"
import CanNotDeleteClient from "./Modals/CanNotDeleteClient"
import DeleteClient from "./Modals/DeleteClient"
import { toaster } from "components/common/Toast"
import { createConversation } from "store/conversations"
import ClientGroupManage from "./Modals/ClientGroupManage"
import { formatPhoneNumber } from "utils/data-formatters"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"

const ClientTable = ({
  clients,
  sortField,
  sortOrder,
  handleTableChange,
  handleFetchClients,
  selectedGroups,
  setSelectedGroups,
  groupAction,
  setGroupAction,
  selectedClientIds,
  setSelectedClientIds,
  removeGroups,
  setRemoveGroups,
  isMenuListHide,
  setIsMenuListHide,
  selectedClient,
  setSelectedClient,
  handleSelectedGroupsChange,
  hasDeleteClient,
  setHasDeleteClient,
  clientId,
  setClientId,
  selectedRowKeys,
  setSelectedRowKeys
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { hasPermission } = usePermissions()
  const [isGroupModalVisible, setIsGroupModalVisible] = useState(false)
  const [isCanNotDeleteClientVisible, setIsCanNotDeleteClientVisible] = useState(false)
  const [isDeleteClientVisible, setIsDeleteClientVisible] = useState(false)
  const groups = useSelector((state) => state.groups?.items || [])
  const { isClientDeletable, loading: isClientLoading, failed: isFailed, metadata } = useSelector((state) => state.clients)

  const canEditClient = hasPermission(MODULES.CLIENTS, ACTIONS.EDIT)

  useEffect(() => {
    if (!hasDeleteClient || isClientLoading || !clientId || isFailed) {
      return
    }

    if (isClientDeletable) {
      setIsDeleteClientVisible(true)
    } else {
      setIsCanNotDeleteClientVisible(true)
    }

    setHasDeleteClient(false)
  }, [hasDeleteClient, isClientLoading, isClientDeletable, clientId, isFailed, setHasDeleteClient])

  const handleDeleteClick = async () => {
    try {
      setIsDeleteClientVisible(false)
      setIsCanNotDeleteClientVisible(false)
      const response = await dispatch(deleteClient(clientId))
      toaster.success({
        title: "Client Details",
        text: response?.message || "Client deleted successfully"
      })
      handleFetchClients()
    } catch (error) {
      console.error(error)
      toaster.error({
        title: "Client Details",
        text: "Failed to delete client"
      })
    }
  }

  const handleGroupActionClick = () => {
    if (groupAction === "bulk_add" || groupAction === "add") {
      handleGroupSaveClick()
    } else if (groupAction === "bulk_remove" || groupAction === "remove") {
      handleRemoveGroup()
    }
  }

  const handleMenuClick = (type, client) => {
    const userIds = [client?.user_id]
    const templates = metadata[`${type}_templates_collection`]
    switch (type) {
      case "email":
        navigate(routes.guideClientEmailActionPath(), { state: { clients, userIds, templates } })
        break
      case "text":
        navigate(routes.guideClientTextActionPath(), { state: { clients, userIds, templates } })
        break
      default:
        navigate(routes.guideClientMessageActionPath(), { state: { clients, userIds, templates } })
    }
  }

  const getMenuItems = (client) => {
    const menuItems = [
      {
        key: "view",
        label: t("clients.menu_items.view"),
        onClick: () => {
          navigate(routes.guideClientPath({ id: client.id }))
        }
      }
    ]

    if (canEditClient) {
      menuItems.push({
        key: "edit",
        label: t("clients.menu_items.edit"),
        onClick: () => {
          dispatch(cleanClient()).then(() => navigate(routes.guideClientEditPath({ id: client.id })))
        },
        disabled: !client?.invited_by_guide
      })
    }

    if (canEditClient) {
      menuItems.push({
        key: "delete",
        label: t("clients.menu_items.delete"),
        onClick: () => {
          setHasDeleteClient(true)
          setClientId(client.id)
          dispatch(CheckClientDelete(client.id))
        }
      })
    }

    menuItems.push(
      {
        key: "add_to_group",
        label: t("clients.menu_items.add_to_group"),
        onClick: () => {
          setSelectedClient(client)
          setGroupAction("add")
          setIsGroupModalVisible(true)
          setSelectedClientIds([client.id])
          setSelectedGroups(client.client_groups || [])
          setRemoveGroups([])
        },
        disabled: !canEditClient
      },
      {
        key: "remove_from_group",
        label: t("clients.menu_items.remove_from_group"),
        onClick: () => {
          setSelectedClient(client)
          setGroupAction("remove")
          setIsGroupModalVisible(true)
          setIsMenuListHide(true)
          setSelectedClientIds([client.id])
          setSelectedGroups(client.client_groups || [])
          setRemoveGroups(client.client_groups || [])
        },
        disabled: !canEditClient || !client.client_groups || client.client_groups.length === 0
      },
      {
        key: "email",
        label: t("clients.menu_items.email"),
        onClick: () => handleMenuClick("email", client)
      },
      {
        key: "message",
        label: t("clients.menu_items.message"),
        onClick: () => {
          dispatch(createConversation({ participant_id: client.user.id })).then((conversation) =>
            navigate(routes.guideMessagesPath({}, { conversation_id: conversation.id }))
          )
        }
      },
      {
        key: "text",
        label: t("clients.menu_items.text"),
        onClick: () => handleMenuClick("text", client),
        disabled: !client.user?.phone_number
      }
    )

    return menuItems
  }

  const dataSource = useMemo(() => {
    return clients.map((client) => {
      const { id, user_id, total_booking_amount, last_trip_date, user, last_booked_trip_title } = client || {}
      const { phone_number, first_name, last_name, email } = user || {}
      return {
        key: user_id,
        client_id: id,
        name: (
          <Flex gap={16} align="center">
            <Avatar user={user} size={44} border={false} fit={false} />
            <Text className="ellipsis" title={`${first_name} ${last_name}`} style={{ maxWidth: "140px" }}>
              {first_name} {last_name}
            </Text>
          </Flex>
        ),
        group: (
          <GroupTags
            setIsGroupModalVisible={setIsGroupModalVisible}
            setSelectedGroups={setSelectedGroups}
            setGroupAction={setGroupAction}
            setSelectedClientIds={setSelectedClientIds}
            client={client}
            setRemoveGroups={setRemoveGroups}
            canEditClient={canEditClient}
          />
        ),
        email: email ? (
          <button type="button" className={`${styles.contactLink} ellipsis`} onClick={(e) => handleMenuClick("email", client)}>
            {email}
          </button>
        ) : null,
        phone: phone_number ? (
          <button type="button" className={`${styles.contactLink} ellipsis`} onClick={(e) => handleMenuClick("text", client)}>
            {formatPhoneNumber(phone_number)}
          </button>
        ) : null,
        last_booked_trip_title: last_booked_trip_title,
        experience_date: last_trip_date,
        ytd_spend: total_booking_amount,
        actions: (
          <Dropdown
            menu={{
              items: getMenuItems(client)
            }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <MoreOutlined className={styles.moreIcon} />
          </Dropdown>
        )
      }
    })
  }, [clients, t])

  const rowSelectionConfig = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys, selectedRows) => {
      setSelectedRowKeys(newSelectedRowKeys)
    }
  }

  const hasSelected = selectedRowKeys.length > 0

  const handleBulkGroupAction = (action, selectedKeys) => {
    const selectedClients = clients.filter((client) => selectedKeys.includes(client.user_id))

    const clientIds = selectedClients.map((client) => client.id)
    const clientGroups = selectedClients.flatMap((client) => client.client_groups || [])

    const uniqueGroupsMap = new Map()
    for (const group of [...selectedGroups, ...clientGroups]) {
      if (group?.group_id && !uniqueGroupsMap.has(group.group_id)) {
        uniqueGroupsMap.set(group.group_id, group)
      }
    }

    setSelectedClientIds(clientIds)
    setSelectedGroups(Array.from(uniqueGroupsMap.values()))
    setGroupAction(action)
    setIsGroupModalVisible(true)
    setIsMenuListHide(action === "bulk_remove")
    setSelectedClient(null)
    setRemoveGroups([])
  }

  const onCanNotDeleteClientClose = () => {
    setIsCanNotDeleteClientVisible(false)
  }

  const onDeleteClientClose = () => {
    setIsDeleteClientVisible(false)
  }

  const handleViewBookingClick = () => {
    setIsCanNotDeleteClientVisible(false)
    navigate(routes.guideBookingsPath())
  }

  const handleGroupSaveClick = async () => {
    try {
      await dispatch(
        SyncBulkGroup({
          client_ids: selectedClientIds,
          group_ids: selectedGroups.map((group) => group.group_id),
          remove_group_ids: removeGroups.map((group) => group.group_id)
        })
      )
      handleCancelGroupAction()
    } catch (error) {
      console.error("Failed to bulk add client groups:", error)
    }
  }

  const handleRemoveGroup = async () => {
    if (selectedClientIds?.length) {
      try {
        const data = {
          client_ids: selectedClientIds,
          // group_ids: selectedGroups.map((group) => group.group_id),
          remove_group_ids: removeGroups.map((group) => group.group_id)
        }

        await dispatch(SyncBulkGroup(data))
        handleCancelGroupAction()
      } catch (error) {
        console.error("Failed to bulk delete client groups:", error)
      }
    }
  }

  const handleCancelGroupAction = () => {
    setIsGroupModalVisible(false)
    setSelectedClient(null)
    setGroupAction("")
    setIsMenuListHide(false)
    setSelectedClientIds([])
    setSelectedGroups([])
    handleFetchClients()
  }

  // Attach a global navigation function for name click in table
  if (typeof window !== "undefined") {
    window.__navigateToClient = (clientKey) => {
      navigate(routes.guideClientPath({ id: clientKey }))
    }
  }

  return (
    <>
      <div>
        {hasSelected && (
          <BulkActions
            clients={clients}
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
            onGroupAction={handleBulkGroupAction}
            canEditClient={canEditClient}
          />
        )}
        <DataTable
          columns={getHeaders({ field: sortField, order: sortOrder }, t)}
          dataSource={dataSource}
          rowSelection={rowSelectionConfig}
          pagination={false}
          rowClickKey="key"
          onChange={handleTableChange}
          rowKey={(record) => record.key}
        />
      </div>

      {isGroupModalVisible && (
        <ClientGroupManage
          groupAction={groupAction}
          isGroupModalVisible={isGroupModalVisible}
          setIsGroupModalVisible={setIsGroupModalVisible}
          selectedClient={selectedClient}
          selectedClientIds={selectedClientIds}
          groups={groups}
          handleSelectedGroupsChange={handleSelectedGroupsChange}
          handleGroupActionClick={handleGroupActionClick}
          isMenuListHide={isMenuListHide}
          setSelectedClientIds={setSelectedClientIds}
          selectedGroups={selectedGroups}
          handleCancelGroupAction={handleCancelGroupAction}
          removeGroups={removeGroups}
        />
      )}

      {isCanNotDeleteClientVisible && (
        <CanNotDeleteClient
          open={isCanNotDeleteClientVisible}
          onClose={onCanNotDeleteClientClose}
          handleViewBookingClick={handleViewBookingClick}
        />
      )}
      {isDeleteClientVisible && (
        <DeleteClient
          open={isDeleteClientVisible}
          onClose={onDeleteClientClose}
          handleDeleteClick={handleDeleteClick}
          isClientLoading={isClientLoading}
        />
      )}
    </>
  )
}

ClientTable.propTypes = {
  clients: PropTypes.array.isRequired,
  sortField: PropTypes.string,
  sortOrder: PropTypes.string,
  handleTableChange: PropTypes.func,
  handleFetchClients: PropTypes.func,
  selectedGroups: PropTypes.array,
  setSelectedGroups: PropTypes.func,
  selectedClientIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  setSelectedClientIds: PropTypes.func,
  selectedClient: PropTypes.object,
  setSelectedClient: PropTypes.func,
  removeGroups: PropTypes.array,
  setRemoveGroups: PropTypes.func,
  isMenuListHide: PropTypes.bool,
  setIsMenuListHide: PropTypes.func,
  groupAction: PropTypes.string,
  setGroupAction: PropTypes.func,
  hasDeleteClient: PropTypes.bool,
  setHasDeleteClient: PropTypes.func,
  clientId: PropTypes.string,
  setClientId: PropTypes.func,
  setSelectedRowKeys: PropTypes.func,
  selectedRowKeys: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  handleSelectedGroupsChange: PropTypes.func
}

export default ClientTable
